import { FC } from 'react';

import SectionContainer from '@common/components/SectionContainer';
import { Routes } from '@common/constants';
import { useCheckForArrivalDate } from '@common/hooks';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useSpaSearchComplete } from '@features/JourneyPickerV2/useSpaSearchComplete';
import { useGetSpaCategories } from '@features/spa/hooks/useGetSpaCategories';
import { buildCategoryOptions } from '@features/spa/utils/buildCategoryOptions';
import { useTranslations } from '@lang';
import { useRouter } from 'shared-routing';

import { SpaCategoryListTemplate, SpaListTemplate } from 'voyager';

import { useGetSpaSettings } from '@features/spa/hooks/useGetSpaSettings';
import { mapCategoriesToCards } from '@features/spa/utils/mapCategoriesToCards';

import { useSpaEntityFilter } from '@features/spa/hooks/useSpaEntityFilter';
import { useSpaTreatmentsList } from './hooks/useSpaTreatmentsList';
import { SpaTreatmentsV2Props } from './SpaTreatmentsV2.types';

export const SpaTreatmentsV2: FC<SpaTreatmentsV2Props> = ({
  property,
  journeyParams,
  categoryId = null,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.SPA_TREATMENT_LIST,
  });

  const filter = useSpaEntityFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
  });

  const handleSearchComplete = useSpaSearchComplete();

  const { listItems, isFetching } = useSpaTreatmentsList({
    propertyId: property.id,
    language,
    categoryFilterId: filter.categoryFilterId,
    date: filter.appliedDateString,
  });

  const { data: treatmentsCategories = [], isFetching: isFetchingCategories } = useGetSpaCategories(
    property.id,
    'treatments',
    language
  );

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const isTreatmentCategoryViewVisible = spaSettings?.isTreatmentCategoryViewVisible ?? false;

  const categoryOptions = buildCategoryOptions(
    treatmentsCategories,
    t('spa.package.allCategories')
  );

  const handleSelectTreatment = (id: string) => {
    router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } });
  };

  const handleSelectCategory = (id: string) => {
    router.push({ pathname: Routes.SPA_TREATMENT_CATEGORY_DETAIL, query: { id } });
  };

  const titleLabel = t('app.spaTreatments');

  return (
    <EntitiesListingWrapper>
      {(!isTreatmentCategoryViewVisible || treatmentsCategories.length === 0) && (
        <JourneyPickerOuterWrapper>
          <JourneyPickerInnerWrapper>
            <JourneyPickerV2
              dateMode="single"
              dateContext="spa"
              categories={navCategories}
              category="treatments"
              onCategoryChange={handleNavCategoryChange}
              onSearchComplete={handleSearchComplete}
            />
          </JourneyPickerInnerWrapper>
        </JourneyPickerOuterWrapper>
      )}
      <SectionContainer>
        {isTreatmentCategoryViewVisible && treatmentsCategories.length > 0 ? (
          <SpaCategoryListTemplate
            categories={mapCategoriesToCards(treatmentsCategories, 'treatments')}
            onSelectCategory={handleSelectCategory}
            isLoading={isFetchingCategories}
            titleLabel={titleLabel}
            fromPriceLabel={t('common.from')}
            noResultsLabel={t('spa.noResults')}
            noImageLabel={t('spa.noImage')}
          />
        ) : (
          <SpaListTemplate
            items={isFetching ? [] : listItems}
            onSelectItem={handleSelectTreatment}
            isLoading={isFetching}
            categoryOptions={categoryOptions}
            selectedCategory={filter.selectedCategory || ''}
            onCategoryChange={filter.handleDirectCategoryChange}
            titleLabel={titleLabel}
            categoryLabel={t('common.category')}
            fromPriceLabel={t('common.from')}
            soldOutLabel={t('spa.soldOut')}
            noResultsLabel={t('spa.noResults')}
            noImageLabel={t('spa.noImage')}
            formatGuests={(n) => t('common.numberOfGuests', { guestsAmount: n })}
          />
        )}
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
